import { useEffect, useRef } from 'react'

import { subscribeToLocationPin } from '@/map'
import { gis } from '@/server-data'

import { useDepotsAsMap } from './useDepotsAsMap'

import { resolveConnectedDepotId } from '../resolveConnectedDepotId'
import { LocationEditorFieldConfig, LocationEditorFieldValue } from '../types'

type OnChange = (value: LocationEditorFieldValue) => void

export function useConnectLocationPinToFormField(
  props: LocationEditorFieldConfig,
  location: LocationEditorFieldValue,
  onChange: OnChange,
) {
  const { pinId, disabled, onRadiusChanged, pinRadius, onConnectedPlaceChanged, connectedPlaceId } =
    props

  const depotsAsMap = useDepotsAsMap()

  const api = useRef({
    formLocation: location,
    onChange,
    onRadiusChanged,
    pinRadius,
    onConnectedPlaceChanged,
    connectedPlaceId,
    depotsAsMap,
    // True once the pin entered a loading cycle (drag / reverse-geocode). Lets us
    // tell a user pin move apart from a depot picked in the dropdown, which never
    // sets `loading` and owns the depot identity itself.
    pinWasLoading: false,
  })
  useEffect(() => {
    api.current = {
      formLocation: location,
      onChange,
      onRadiusChanged,
      pinRadius,
      onConnectedPlaceChanged,
      connectedPlaceId,
      depotsAsMap,
      pinWasLoading: api.current.pinWasLoading,
    }
  }, [
    location,
    onChange,
    onRadiusChanged,
    pinRadius,
    onConnectedPlaceChanged,
    connectedPlaceId,
    depotsAsMap,
  ])

  // listen to pin changes
  useEffect(() => {
    if (disabled) return
    let effectCleared = false

    const unsubscribe = subscribeToLocationPin(pinId, pin => {
      if (effectCleared) return
      if (!pin) return

      // A pin drag enters a loading cycle AND moves the pin's coordinates up front
      // (see moveLocationPin). A dropdown place search / initial pin setup also set
      // `loading`, but WITHOUT moving latLng. Only treat the coordinate-moving case
      // as a user pin move, so a place selection (whose connection the dropdown
      // deliberately cleared) isn't misread and re-connected to a depot at that spot.
      if (pin.loading) {
        const { formLocation } = api.current
        if (formLocation && !gis.latLngEquals(formLocation.latLng, pin.pin.latLng)) {
          api.current.pinWasLoading = true
        }
        return
      }

      const { formLocation, onChange } = api.current
      const location = pin.pin.location

      const cameFromPinMove = api.current.pinWasLoading
      api.current.pinWasLoading = false

      if (!location) return
      if (!formLocation) return

      // Update Form Location if the pin has been moved
      if (
        !gis.latLngEquals(formLocation.latLng, location.latLng) ||
        formLocation.address !== location.address ||
        formLocation.geoAddress !== location.geoAddress ||
        formLocation.status !== location.status
      ) {
        // update form location
        onChange(location)

        const { onConnectedPlaceChanged, connectedPlaceId, depotsAsMap } = api.current
        if (onConnectedPlaceChanged && cameFromPinMove) {
          // The pin was moved by the user (drag) - sync the connection to whatever
          // depot sits at the new coordinates (see resolveConnectedDepotId).
          const nextConnectedPlaceId = resolveConnectedDepotId(
            depotsAsMap,
            location.rawLatLngAsString,
            connectedPlaceId,
          )
          if (connectedPlaceId !== nextConnectedPlaceId) {
            onConnectedPlaceChanged(nextConnectedPlaceId)
          }
        }
      }

      const { onRadiusChanged, pinRadius } = api.current

      // Update Form radius if the pin radius has been changed
      if (onRadiusChanged && typeof pinRadius === 'number' && pinRadius !== pin.pin.radius) {
        onRadiusChanged(pin.pin.radius, pinRadius)
      }
    })

    return () => {
      effectCleared = true
      unsubscribe()
    }
  }, [pinId, disabled])
}
